import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import SegmentedControl from '@bufferapp/ui/SegmentedControl';
import { getMatch, calendarWeek } from '~/routes';
import ComposerPopover from '~/composer-popover';
import { Button, Text } from '@bufferapp/ui';

import { setLastVisitedPage } from '~/utils/page-visits';
import { CALENDAR_PAGE } from '~/constants';
import { ThreadsFreeOBPaywall } from '~/composer/composer/components/ThreadsFreeOBPaywall';
import CalendarNavigation from '../CalendarNavigation';
import DayOfWeekHeader from '../DayOfWeekHeader';
import DaysOfMonth from '../DaysOfMonth';
import DaysOfWeek from '../DaysOfWeek';
import {
  CalendarPage,
  CalendarGrid,
  CalendarTable,
  CalendarHeader,
  CalendarHeaderRightSide,
  CalendarNotice,
} from './style';
import useNavigation from '../hooks/useNavigation';
import useHandleScroll from '../hooks/useHandleScroll';
import useFilterByChannel from '../hooks/useFilterByChannel';
import useChannelPermissions from '../hooks/useChannelPermissions';
import Timezone from '../Timezone';
import ChannelFilter from '../ChannelFilter';
import { TypeFilter } from '../TypeFilter';
import CreateCalendarPostContext from '../hooks/createCalendarPostContext';

const options = [
  {
    label: 'Week',
    value: 'week',
  },
  {
    label: 'Month',
    value: 'month',
  },
];

const Calendar = ({
  location,
  hasTwentyFourHourTimeFormat,
  onWeekClick,
  onMonthClick,
  resetLocationState,
  profilesFromRedux,
  showComposer,
  onCreatePostClick,
  onConfirmDismissComposer,
  onComposerCreateSuccess,
  onCalendarChangeTrack,
  trackChannelsFiltered,
  onDropPostSuccess,
  onDropPostError,
}) => {
  const isWeekly = !!getMatch({
    pathname: location.pathname,
    route: calendarWeek.route,
  });
  const channelSelectedThroughRoute = location.state?.channel;
  const [selected, setSelected] = useState(isWeekly ? 'week' : 'month');

  const navigation = useNavigation();
  const handleScroll = useHandleScroll();
  const channelFilter = useFilterByChannel();
  const channelPermissions = useChannelPermissions({
    channelsSelected: channelFilter.channelsSelected,
    listOfChannels: profilesFromRedux,
  });

  const postButtonDisabled = channelPermissions.isViewOnly;
  const showChannelFilter = profilesFromRedux && !!profilesFromRedux.length;

  useEffect(() => {
    // Reset channel to select state included in some location changes
    if (channelSelectedThroughRoute) {
      resetLocationState({ pathname: location.pathname });
    }
    setLastVisitedPage(CALENDAR_PAGE);
  }, []);

  const handleWeekMonthToggle = (value) => {
    setSelected(value);
    if (value === 'week') onWeekClick(isWeekly);
    else if (value === 'month') onMonthClick(isWeekly);
  };

  const handlePreviousWeek = () => {
    onCalendarChangeTrack('week', 'previous_week');
    navigation.prevWeek();
  };

  const handleNextWeek = () => {
    onCalendarChangeTrack('week', 'next_week');
    navigation.nextWeek();
  };

  const handlePreviousMonth = () => {
    onCalendarChangeTrack('month', 'previous_month');
    navigation.prevMonth();
  };

  const handleNextMonth = () => {
    onCalendarChangeTrack('month', 'next_month');
    navigation.nextMonth();
  };

  const handleToday = () => {
    const source = isWeekly ? 'week' : 'month';
    onCalendarChangeTrack(source, 'today');
    navigation.setCurrentDate(new Date());
  };

  const [openModal, setOpenModal] = useState({
    open: false,
    ctaString: null,
  });

  return (
    <CalendarPage>
      {showComposer && (
        <ComposerPopover
          type="composerPopover"
          onSave={onComposerCreateSuccess}
          onConfirmDismissComposer={onConfirmDismissComposer}
        />
      )}
      {openModal?.open && (
        <ThreadsFreeOBPaywall
          closeFirstModal={() => setOpenModal({ open: false, ctaString: null })}
          ctaString={openModal.ctaString}
        />
      )}
      <CalendarHeader>
        <CalendarNavigation
          currentDate={navigation.currentDate}
          isWeekly={isWeekly}
          onPreviousClick={() =>
            isWeekly ? handlePreviousWeek() : handlePreviousMonth()
          }
          onNextClick={() => (isWeekly ? handleNextWeek() : handleNextMonth())}
          onTodayClick={handleToday}
          loading={false} // dynamically change when we have data
        />
        <CalendarHeaderRightSide>
          <Timezone />
          <TypeFilter />
          {showChannelFilter && (
            <ChannelFilter
              channelFilter={channelFilter}
              channelSelected={channelSelectedThroughRoute}
              isWeekly={isWeekly}
              isViewOnly={channelPermissions.isViewOnly}
              trackChannelsFiltered={trackChannelsFiltered}
            />
          )}
          <SegmentedControl>
            {options.map(({ label, value }) => (
              <SegmentedControl.Option
                key={value}
                optionType="text"
                label={label}
                value={value}
                selected={value === selected}
                onClick={handleWeekMonthToggle}
              />
            ))}
          </SegmentedControl>
          <Button
            type="primary"
            label="Create Post"
            onClick={() =>
              onCreatePostClick({
                selectedProfileId: null,
                selectedProfileIds: channelFilter?.channelsSelected,
              })
            }
            disabled={postButtonDisabled}
          />
        </CalendarHeaderRightSide>
      </CalendarHeader>
      {channelPermissions.isViewOnly && (
        <CalendarNotice type="warning" disableAnimation>
          <Text>
            Looks like you don’t have full posting permissions for all channels.
            Visit individual Queues to create or manage posts.
          </Text>
        </CalendarNotice>
      )}
      <CalendarGrid ref={handleScroll.childRef}>
        <CalendarTable
          showRightAnimation={navigation.rightAnimation}
          showLeftAnimation={navigation.leftAnimation}
          onAnimationEnd={() => {
            if (navigation.rightAnimation) {
              navigation.setRightAnimation(false);
            }
            if (navigation.leftAnimation) {
              navigation.setLeftAnimation(false);
            }
          }}
        >
          <tbody>
            <CreateCalendarPostContext.Provider
              value={{
                hasPermissions: !postButtonDisabled,
                selectedProfileIds: channelFilter?.channelsSelected,
              }}
            >
              {isWeekly ? (
                <DaysOfWeek
                  currentDate={navigation.currentDate}
                  parentRef={handleScroll.childRef}
                  channelsSelected={channelFilter.channelsSelected}
                  hasTwentyFourHourTimeFormat={hasTwentyFourHourTimeFormat}
                  isViewOnly={channelPermissions.isViewOnly}
                  onDropPostSuccess={onDropPostSuccess}
                  onDropPostError={onDropPostError}
                  setOpenModal={setOpenModal}
                />
              ) : (
                <DaysOfMonth
                  currentDate={navigation.currentDate}
                  channelsSelected={channelFilter.channelsSelected}
                  hasTwentyFourHourTimeFormat={hasTwentyFourHourTimeFormat}
                  isViewOnly={channelPermissions.isViewOnly}
                  onDropPostSuccess={onDropPostSuccess}
                  onDropPostError={onDropPostError}
                  setOpenModal={setOpenModal}
                />
              )}
            </CreateCalendarPostContext.Provider>
          </tbody>
          <DayOfWeekHeader
            currentDate={navigation.currentDate}
            withDay={isWeekly}
            headerShadow={handleScroll.headerShadow}
          />
        </CalendarTable>
      </CalendarGrid>
    </CalendarPage>
  );
};

Calendar.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    state: PropTypes.shape({
      channel: PropTypes.string,
    }),
  }).isRequired,
  hasTwentyFourHourTimeFormat: PropTypes.bool,
  onWeekClick: PropTypes.func.isRequired,
  onMonthClick: PropTypes.func.isRequired,
  resetLocationState: PropTypes.func.isRequired,
  profilesFromRedux: PropTypes.arrayOf(PropTypes.object).isRequired,
  showComposer: PropTypes.bool,
  onCreatePostClick: PropTypes.func.isRequired,
  onComposerCreateSuccess: PropTypes.func.isRequired,
  onConfirmDismissComposer: PropTypes.func.isRequired,
  onCalendarChangeTrack: PropTypes.func.isRequired,
  trackChannelsFiltered: PropTypes.func.isRequired,
  onDropPostSuccess: PropTypes.func.isRequired,
  onDropPostError: PropTypes.func.isRequired,
};

Calendar.defaultProps = {
  showComposer: false,
  hasTwentyFourHourTimeFormat: false,
};

export default Calendar;
